import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import type { IFetchOneVisualizationResponse } from '@/src/api/types/backend/visualization.api'
import { useSeriesContext } from '@/src/contexts/SeriesContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { resolveTimeSeriesLabel } from '@/src/types/timeSeriesSelection.interface'
import { ONE_MINUTE_IN_MS } from '@/src/utils/constants'
import { alignSpotMarketSeriesRows, getSpotMarketRequestRange, shouldAlignSpotMarketSeries } from '@/src/utils/spotMarketPrice.utils'
import { useQuery } from '@tanstack/react-query'
import { subDays } from 'date-fns'
import { t } from 'i18next'
import { useCallback } from 'react'
import { VisualizationDataViewEnum } from '../../enums/visualizationDataView.enum'
import type { IVisualizationData } from '../../types/visualizationData.interface'

const initialData: IVisualizationData = {
  id: '',
  formData: {
    defaultView: VisualizationDataViewEnum.TABLE,
    name: '',
    visualizationDateRange: [subDays(new Date(), 1).toISOString(), new Date().toISOString()],
    granularity: 'loadProfile',
    timeSeriesIds: [],
  },
  graphData: {
    granularity: '',
    leftYAxisLabel: '',
    rightYAxisLabel: '',
    series: [],
    dataPoints: [],
  },
  tableData: {
    granularity: '',
    series: [],
    rows: [],
  },
  kpiData: {
    series: [],
  },
}

export const granularityValueMap = {
  [GranularityEnum.ONE_MINUTE]: 1,
  [GranularityEnum.FIVE_MINUTES]: 5,
  [GranularityEnum.FIFTEEN_MINUTES]: 15,
  [GranularityEnum.THIRTY_MINUTES]: 30,
  [GranularityEnum.ONE_HOUR]: 60,
  [GranularityEnum.ONE_DAY]: 1440,
  [GranularityEnum.ONE_WEEK]: 10080,
  [GranularityEnum.ONE_MONTH]: 43200,
}

export const useVisualizationData = ({ id }: { id?: string }) => {
  const { series } = useSeriesContext()
  const toast = useBaseToast()

  const queryKey = queryKeys.analytics.visualizations.oneById.toKeyWithArgs(id)
  const query = useQuery<any, any, IVisualizationData>({
    queryKey,
    enabled: !!id,
    staleTime: ONE_MINUTE_IN_MS * 5,
    retry: 0,

    /*  The return value of this function will be cached by react-query */
    queryFn: async () => {
      if (!id) return null
      try {
        const response = await API.visualization.fetchOne({ id })
        const visualization = response.data
        const seriesIds = visualization.parameters?.timeseries?.map(timeseries => timeseries.id) ?? []
        const seriesData = visualization.seriesData ?? []

        if (!shouldAlignSpotMarketSeries(seriesIds, visualization.parameters?.granularity)) return visualization

        const spotMarketRequestRange = getSpotMarketRequestRange(seriesData, visualization.parameters.granularity)
        if (!spotMarketRequestRange) return visualization

        const spotPrices = await API.spotMarket.fetchPrices(spotMarketRequestRange)

        return {
          ...visualization,
          seriesData: alignSpotMarketSeriesRows(seriesData, seriesIds, spotPrices, visualization.parameters.granularity),
        }
      } catch (err) {
        toast({
          id: 'loading-visualization-error',
          status: 'error',
          description: t('analytics.error_loading_visualization') as string,
        })
        throw err
      }
    },

    /* The return value of this function will NOT be cached by react-query */
    select: useCallback(
      (data: IFetchOneVisualizationResponse): IVisualizationData => {
        if (!data) return initialData

        const { parameters, id: visualizationId, kpis = {}, seriesData = [], units = {} } = data as any
        const timeseries = parameters?.timeseries ?? []

        type UnitShape =
          | Record<string, IVisualizationData['graphData']['series'][number]['unit'] | undefined>
          | Array<IVisualizationData['graphData']['series'][number]['unit'] | undefined>
        const unitsRecord = units as UnitShape

        type KpiSeries = IVisualizationData['kpiData']['series'][number]
        type KpisShape = {
          series?: KpiSeries[]
          correlationCoefficient?: number
          [key: string]: KpiSeries | KpiSeries[] | number | undefined
        }
        const kpisRecord = kpis as KpisShape

        const getUnitForSeries = (index: number, seriesId?: string) => {
          const indexedKey = `series${index + 1}`
          if (Array.isArray(unitsRecord)) return unitsRecord[index]

          const unitByIndexedKey = unitsRecord[indexedKey]
          if (unitByIndexedKey) return unitByIndexedKey

          if (seriesId) {
            const unitBySeriesId = unitsRecord[seriesId]
            if (unitBySeriesId) return unitBySeriesId
          }

          return undefined
        }

        const getKpiForSeries = (index: number, seriesId?: string) => {
          const indexedKey = `series${index + 1}`
          if (Array.isArray(kpisRecord.series)) return kpisRecord.series[index]

          const kpiByIndexedKey = kpisRecord[indexedKey]
          if (kpiByIndexedKey && typeof kpiByIndexedKey === 'object' && !Array.isArray(kpiByIndexedKey)) return kpiByIndexedKey as KpiSeries

          if (seriesId) {
            const kpiBySeriesId = kpisRecord[seriesId]
            if (kpiBySeriesId && typeof kpiBySeriesId === 'object' && !Array.isArray(kpiBySeriesId)) return kpiBySeriesId as KpiSeries
          }

          return undefined
        }

        const seriesMeta = timeseries.map((timeserie: any, index: number) => {
          const dataKey = `timeSeries${index + 1}`
          return {
            id: timeserie.id,
            name: resolveTimeSeriesLabel(timeserie.id, series),
            dataKey,
            unit: getUnitForSeries(index, timeserie.id),
          }
        })

        const getUnitIdentity = (unit: any) => unit?.entries ?? unit?.unit ?? JSON.stringify(unit ?? {})
        const unitAxisMap = new Map<string, 'left' | 'right'>()
        const seriesWithAxis = seriesMeta.map(meta => {
          const identity = getUnitIdentity(meta.unit)
          if (!unitAxisMap.has(identity)) {
            const axisId: 'left' | 'right' = unitAxisMap.size === 0 ? 'left' : unitAxisMap.size === 1 ? 'right' : 'left'
            unitAxisMap.set(identity, axisId)
          }
          return { ...meta, yAxisId: unitAxisMap.get(identity) ?? 'left' }
        })

        const dataPoints = (seriesData || []).map((dataPoint: any) => {
          const basePoint: any = { date: dataPoint?.date ? new Date(dataPoint.date) : new Date() }

          seriesWithAxis.forEach(meta => {
            const possibleKeys = [meta.dataKey, meta.id]
            let value: number | null | undefined

            for (const key of possibleKeys) {
              if (dataPoint?.[key] !== undefined) {
                value = dataPoint[key]
                break
              }
            }

            if (value === undefined && dataPoint?.values) {
              if (meta.id && dataPoint.values[meta.id] !== undefined) {
                value = dataPoint.values[meta.id]
              } else if (dataPoint.values[meta.dataKey] !== undefined) {
                value = dataPoint.values[meta.dataKey]
              }
            }

            basePoint[meta.dataKey] = value
          })

          return basePoint
        })

        const leftYAxisLabel = seriesWithAxis.find(meta => meta.yAxisId === 'left')?.unit?.entries ?? ''
        const rightYAxisLabel = seriesWithAxis.find(meta => meta.yAxisId === 'right')?.unit?.entries

        const kpiSeries = seriesWithAxis
          .map((meta, index) => {
            const kpiForSeries = getKpiForSeries(index, meta.id)
            if (!kpiForSeries) return null

            return {
              id: meta.id,
              name: meta.name,
              ...kpiForSeries,
              // prefer dedicated KPI unit, fall back to series unit if none provided
              unit: meta.unit?.kpi ?? kpiForSeries.unit ?? meta.unit,
            }
          })
          .filter(Boolean) as IVisualizationData['kpiData']['series']

        const timeframeStart =
          parameters?.timeframe_start !== undefined ? String(parameters.timeframe_start) : initialData.formData.visualizationDateRange[0]
        const timeframeEnd =
          parameters?.timeframe_end !== undefined ? String(parameters.timeframe_end) : initialData.formData.visualizationDateRange[1]
        const resolvedGranularity = parameters?.granularity ?? ''
        const resolvedDefaultView = parameters?.chart_type ?? VisualizationDataViewEnum.TABLE
        const resolvedName = parameters?.title ?? ''

        const visualizationData: IVisualizationData = {
          id: visualizationId,
          formData: {
            defaultView: resolvedDefaultView,
            name: resolvedName,
            granularity: [GranularityEnum.FIFTEEN_MINUTES, GranularityEnum.THIRTY_MINUTES, GranularityEnum.ONE_HOUR].includes(
              parameters?.granularity
            )
              ? 'loadProfile'
              : resolvedGranularity,
            visualizationDateRange: [timeframeStart, timeframeEnd],
            timeSeriesIds: timeseries.map((timeseries: any) => timeseries.id),
          },
          kpiData: {
            series: kpiSeries,
            correlationCoefficient: kpis?.correlationCoefficient,
          },
          graphData: {
            leftYAxisLabel,
            rightYAxisLabel,
            series: seriesWithAxis,
            granularity: resolvedGranularity,
            dataPoints,
          },
          tableData: {
            granularity: resolvedGranularity,
            series: seriesWithAxis,
            rows: dataPoints,
          },
        }

        return visualizationData
      },
      [id, series]
    ),
  })

  return { ...query, data: query.data ?? initialData }
}
